<template>
	<div class="sr" v-observe-visibility="{ callback: onVisibilityPlaybackControl }" @click="emits('onPosterClickOut')">
		<div class="sr__container radius-xsmall border-100 border-high-active bg-low-active">
			<div
				class="sr__mute-control radius-xsmall ms-2"
				v-if="sraPlacement !== 'BACKDROP_VIDEO' && !isVideoAsGifCampaign"
				@click.stop="toggleMute"
			>
				<img :src="volumeIcon" alt="volume" style="fill: white" />
			</div>
			<div
				class="overlay sr__overlay px-5 py-7"
				:class="{
					'sr__overlay--fading': isFading && !isVideoAsGifCampaign,
					'sr__overlay--gradient': isVideoAsGifCampaign,
				}"
				@mouseenter="onMouseEnter"
				@mouseleave="onMouseLeave"
			>
				<div class="overlay__overlay-wrapper sr__content-container g-1">
					<slot name="campaign-info"></slot>

					<slot name="action-items"></slot>

					<div v-if="disclaimerText" class="sr__disclaimer pt-1">
						{{ disclaimerText }}
					</div>
				</div>
			</div>

			<ClientOnly>
				<video
					ref="videoElement"
					class="title-video__video sr__video-container"
					:class="{
						'sr__video-container--gif-top': shouldPositionVidAsGifToTop,
					}"
					width="100%"
					height="100%"
					autoplay
					:muted="isMuted"
					playsinline
					@play="() => onVideoStateChanged('play')"
					@loadedmetadata="setVideoDuration"
					@timeupdate="onTimeUpdate"
					@ended="onVideoEnded"
					@imageError="$emit('imageError', sponsoredCampaign?.promotionalVideo?.url)"
				>
					<source :src="sponsoredCampaign?.promotionalVideo?.url || ''" type="video/mp4" />
				</video>
			</ClientOnly>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { Maybe } from '@/helpers/types';

import { computed, onMounted, ref, watch } from 'vue';
import { useDocumentVisibility } from '@vueuse/core';

import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

import { SraEventAction, SraPlacement } from '@/@types/graphql-types';
import { ImpressionTrackingEvents } from '@/enums/events';

import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { SingleTitleSR } from '@/features/sponsored-recommendations/types';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

/** Viewing thresholds in percentages at which we want to track. */
type TrackingWatchedPointsInPercentage = (typeof TrackingWatchedPointsInPercentage)[number];
const TrackingWatchedPointsInPercentage = [0, 25, 50, 75, 90, 100] as const;

/** Viewing thresholds in seconds at which we want to track. */
type TrackingWatchedPointsInSeconds = (typeof TrackingWatchedPointsInSeconds)[number];
const TrackingWatchedPointsInSeconds = [2, 6, 15] as const;

const props = withDefaults(
	defineProps<{
		sponsoredAd: SponsoredAdFragment;
		isMuted?: boolean;
		lazy?: boolean;
		showOverlay?: boolean;
		additionalContexts?: SnowplowContext[];
		sraPlacement?: SraPlacement;
	}>(),
	{
		lazy: true,
		isMuted: false,
		showOverlay: false,
		additionalContexts: () => [],
	}
);

const emits = defineEmits<{
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
	(e: 'onVideoEnded', hasVideoEnded: boolean): void;
}>();

const { trackSraEvent } = useBackendTracking();
const visibility = useDocumentVisibility();
const { activeRoute } = storeToRefs(useRoutingStore());

const sponsoredCampaign = computed(() => props.sponsoredAd?.campaign);
const campaignNode = computed<SingleTitleSR | null>(
	() => (sponsoredCampaign.value?.node ?? null) as SingleTitleSR | null
);

const disclaimerText = computed(() => sponsoredCampaign.value?.disclaimerText);

const isVideoAsGifCampaign = computed(() => sponsoredCampaign.value?.playSRVideoAsGif);

// Hack: display the video as gif in top of container for search page placement only
const shouldPositionVidAsGifToTop = computed(() => {
	return activeRoute.value?.name?.includes('search') && isVideoAsGifCampaign.value;
});

/* Watch Document Visibility */
watch(visibility, (current, previous) => {
	if (videoElement && current === 'visible' && previous === 'hidden') {
		videoElement.value?.play();
		return;
	}

	if (videoElement && current === 'hidden' && previous === 'visible') {
		videoElement.value?.pause();
		return;
	}
});

/* VIDEO TRACKING */
const videoElement = ref<HTMLVideoElement | null>(null);

/* VIDEO CONTROL*/
const isFading = ref(false);
const isMuted = ref(true);

const volumeIcon = computed(() => {
	const filename = isMuted.value ? 'volume-xmark' : 'volume-high';
	return `/${ASSETS_DIR}/img/sponsored-recommendation-assets/${filename}.svg`;
});

function onVideoStateChanged(eventName: string) {
	if (eventName !== 'play') return;
	setTimeout(() => (isFading.value = true), 1500);
}

function onMouseEnter() {
	isFading.value = false;
}

function onMouseLeave() {
	isFading.value = true;
}

function onVisibilityPlaybackControl(isVisible: boolean) {
	if (!videoElement.value) return;
	isVisible ? videoElement.value.play() : videoElement.value.pause();
}

function toggleMute() {
	isMuted.value = !isMuted.value;

	if (videoElement.value) {
		videoElement.value.muted = isMuted.value;
	}

	if (!campaignNode.value) {
		return;
	}

	const titleContext = new SnowplowTitleContextGraphql(
		campaignNode.value?.objectId,
		campaignNode.value?.objectType,
		campaignNode.value?.__typename === 'Season' ? campaignNode.value?.content.seasonNumber : null
	);

	// legacy event as long as there doesnt exist a SraEventAction.ToggleMute.
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'toggle_mute',
			value: isMuted.value ? 1 : 0,
		},
		[titleContext]
	);
}

/* Track Duration Play */
const videoDuration = ref(0);
const lastTrackedPointPercentage = ref<TrackingWatchedPointsInPercentage | -1>(-1);
const lastTrackedPointSeconds = ref<TrackingWatchedPointsInSeconds | -1>(-1);

function setVideoDuration() {
	if (!videoElement.value) return;
	videoDuration.value = videoElement.value.duration;
}

function onTimeUpdate() {
	const currentTime = videoElement.value?.currentTime ?? 0;

	/** Percentage Tracking */
	if (lastTrackedPointPercentage.value < 100 && videoDuration.value > 0) {
		const percentageViewed = Math.ceil((currentTime / videoDuration.value) * 100);
		const nextTrackingPointPercent = TrackingWatchedPointsInPercentage.find(
			point => point > lastTrackedPointPercentage.value
		);

		if (nextTrackingPointPercent != null && percentageViewed >= nextTrackingPointPercent) {
			lastTrackedPointPercentage.value = nextTrackingPointPercent;

			trackSraEvent({
				action: SraEventAction.VideoView,
				sponsoredAd: () => props.sponsoredAd,
				progress: nextTrackingPointPercent,
			});
		}
	}

	/** Seconds Tracking */
	if (lastTrackedPointSeconds.value < 15) {
		const secondsViewed = Math.floor(currentTime);
		const nextTrackingPointSeconds = TrackingWatchedPointsInSeconds.find(
			point => point > lastTrackedPointSeconds.value
		);

		if (nextTrackingPointSeconds != null && secondsViewed >= nextTrackingPointSeconds) {
			lastTrackedPointSeconds.value = nextTrackingPointSeconds;

			trackSraEvent({
				action: SraEventAction.VideoViewSeconds,
				sponsoredAd: () => props.sponsoredAd,
				progress: nextTrackingPointSeconds,
			});
		}
	}
}

/**
 * `this.onTimeUpdate` doesn't always fire at 100 before restarting the loop.
 * So we trigger it manually to guarantee it gets tracked.
 */
function onVideoEnded() {
	if (!videoElement.value) return;

	emits('onVideoEnded', true);

	// Replay the video
	videoElement.value.play();
}

// Set the initial muted state based on the prop
onMounted(() => {
	if (props.sraPlacement === 'BACKDROP_VIDEO') {
		isMuted.value = false;
		if (videoElement.value) {
			videoElement.value.muted = false;
		}
	}
});
</script>

<style lang="scss" scoped>
.sr {
	position: relative;
	height: 100%;
	width: 100%;
	cursor: pointer;

	&__container {
		width: 100%;
		height: 100%;
		position: relative;
		display: flex;
		overflow: hidden;
		transition: opacity 0.1s ease;
	}

	&__mute-control {
		position: absolute;
		z-index: 10;
		cursor: pointer;
		top: var(--jw-spacing-2);
		background-color: rgba(47, 56, 65, 0.7);
		width: 32px;
		height: 32px;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 16px;
			height: 16px;
		}
	}

	&__overlay {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		opacity: 1;
		transition: opacity 2s ease-in-out;

		position: absolute;
		z-index: 1;
		top: 0;
		width: 100%;
		height: 100%;

		&:hover {
			transition: opacity 0.25s ease-in-out;
		}

		&--fading {
			opacity: 0;
		}

		// Apply gradient for gif campaigns to show disclaimer text
		&--gradient {
			background: linear-gradient(
				to bottom,
				rgba(0, 0, 0, 0) 0%,
				rgba(0, 0, 0, 0.02) 15%,
				rgba(0, 0, 0, 0.08) 25%,
				rgba(0, 0, 0, 0.2) 35%,
				rgba(0, 0, 0, 0.4) 50%,
				rgba(0, 0, 0, 0.6) 65%,
				rgba(0, 0, 0, 0.75) 80%,
				rgba(0, 0, 0, 0.85) 90%,
				rgba(0, 0, 0, 0.8) 100%
			);
		}
	}

	&__disclaimer {
		font-size: 10px;
		color: var(--jw-shade-white);
	}

	&__video-container {
		object-fit: cover;
		aspect-ratio: 1.5/1;
		&--gif-top {
			object-position: center 9%;
		}
	}

	&__content-container {
		display: flex;
		flex-direction: column;
	}

	@container srCompact (max-width: 360px) {
		&__content-container {
			gap: var(--jw-spacing-half);
		}

		::v-deep(.action-items__providerIcon img.provider-icon) {
			width: var(--jw-size-5);
			height: var(--jw-size-5);
			min-width: var(--jw-size-5);
		}
	}
}

//remove opacity on ios devices as it causes flickering
html.ios .sr__video:active {
	opacity: unset;
}
</style>
